import { Tooltip } from '@neo/primitives/tooltip'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import { pluralize } from '@shared/utils'
import { Eye } from 'lucide-react'
import type { ReactNode } from 'react'
import { NodeName } from '../components/node-name'
import type {
  ChunkMetrics,
  NodeChunkCreatedEvent,
  NodeConfig,
  NodeExecutedEvent,
  SystemChunkCreatedEvent,
} from '../types'
import { isTextAgentChannel } from '../types'
import { KbMetrics } from './kb-metrics'
import { JsonExpandableView } from './tool-call'

type AINodeKey = 'extracted_variables' | 'kb_retrieval_time' | 'kb_chunks'

export type ParsedVariable = {
  variable_name: string
  variable_value: string
  variable_type: string
}

export const parseVariables = (
  variables: NonNullable<NodeConfig['variables']>,
): ParsedVariable[] =>
  Array.isArray(variables)
    ? variables
    : Object.entries(variables).map(([key, value]) => {
        if (value && typeof value === 'object' && 'variable_value' in value) {
          const v = value as { variable_value: string; variable_type: string }
          return {
            variable_name: key,
            variable_value: v.variable_value,
            variable_type: v.variable_type,
          }
        }
        const stringified = typeof value === 'string' ? value : JSON.stringify(value)
        return { variable_name: key, variable_value: stringified, variable_type: 'string' }
      })

export const getAINodeValueMap = (node: NodeExecutedEvent, chunk: NodeChunkCreatedEvent) => {
  const nodeConfig = (node.node_config || {}) as NodeConfig
  const valueMap = new Map<AINodeKey, ReactNode>()

  if (nodeConfig.variables || chunk.node_config?.variables) {
    const variables = nodeConfig.variables || chunk.node_config?.variables || {}
    const variablesArray = parseVariables(variables)

    const variableLength = variablesArray.length
    if (variableLength) {
      valueMap.set(
        'extracted_variables',
        <div className="flex items-center gap-1 rounded-md py-1" key="extracted-variables">
          <span>[{pluralize(variableLength, 'Variable')} Extracted]</span>
          <Tooltip
            className="max-h-80 max-w-[min(32rem,calc(100vw-2rem))] overflow-y-auto border border-border bg-popover p-3 text-popover-foreground"
            delayDuration={0}
            // @ts-expect-error tooltip accepts react node
            content={
              (
                <div className="flex flex-col gap-2 font-jetbrains text-xs-400 break-words [overflow-wrap:anywhere]">
                  {variablesArray.map((variable) => (
                    <div key={variable.variable_name}>
                      {variable.variable_name}: {variable.variable_value}
                    </div>
                  ))}
                </div>
              ) as React.ReactNode
            }
          >
            <button
              type="button"
              aria-label="View extracted variables"
              className="inline-flex size-6 shrink-0 items-center justify-center rounded text-secondary hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="button-view-variables"
            >
              <Eye size={16} aria-hidden />
            </button>
          </Tooltip>
        </div>,
      )
    }
  }
  const kbReferences = chunk.metrics?.kb_metrics?.references || []
  const kbChunks = kbReferences.flatMap((ref) => ref?.chunks || [])
  const kbRetrievalTime = kbReferences.reduce((total, ref) => total + (ref?.time || 0), 0)

  if (kbChunks?.length) {
    valueMap.set(
      'kb_retrieval_time',
      <span>
        [Knowledge base retrival time]:{' '}
        <span className="font-jetbrains text-xs-400 tabular-nums">
          {typeof kbRetrievalTime === 'number' ? `${kbRetrievalTime.toFixed(2)}s` : 'N/A'}
        </span>
      </span>,
    )
    valueMap.set(
      'kb_chunks',
      <span className="flex items-center gap-1">
        [Knowledge Base Chunks Retrieved]
        <KbMetrics chunks={kbChunks} />
      </span>,
    )
  }

  return valueMap
}

type ToolMetric = NonNullable<ChunkMetrics['tool_metrics']>[number]

const toolMetricFromRow = (
  chunk: NodeChunkCreatedEvent | SystemChunkCreatedEvent,
): ToolMetric | null => {
  if (chunk.event_name !== 'SYSTEM_CHUNK_CREATED' || chunk.type !== 'TOOL_CALL_EVENT') return null
  if (chunk.tool_input == null) return null
  return {
    tool_name: chunk.tool_name ?? '',
    tool_input: chunk.tool_input,
    tool_response: chunk.tool_response ?? '',
    time: chunk.time ?? 0,
    tool_call_error: chunk.tool_call_error,
  }
}

export const toolMetricKey = (metric: ToolMetric) => `${metric.tool_name}:${metric.time}`

// Voice tool results are read from the TOOL_CALL_EVENT row: agent-runner can
// commit the reply that announced a tool before the tool returns, so the reply
// may carry no metrics. A message metric is skipped only when a row carries the
// same call; metrics with no row (Sentinel, older mocked sim calls) keep their card.
export const getToolRowKeys = (node: NodeExecutedEvent): Set<string> => {
  const keys = new Set<string>()
  if (isTextAgentChannel(node.node_config?.channel)) return keys
  for (const chunk of node.chunks ?? []) {
    const metric = toolMetricFromRow(chunk)
    if (metric) keys.add(toolMetricKey(metric))
  }
  return keys
}

export const getRowToolMetric = (
  chunk: NodeChunkCreatedEvent | SystemChunkCreatedEvent,
  rowKeys: Set<string>,
) => (rowKeys.size ? toolMetricFromRow(chunk) : null)

export const getChunkToolMetrics = (chunk: NodeChunkCreatedEvent, rowKeys: Set<string>) =>
  (chunk.metrics?.tool_metrics ?? []).filter((metric) => !rowKeys.has(toolMetricKey(metric)))

export const getToolCallValueMaps = (
  node: NodeExecutedEvent,
  toolMetrics: ToolMetric[],
  showNewUI = false,
  hideNodeContext = false,
) => {
  const nodeConfig = (node.node_config || {}) as NodeConfig

  return toolMetrics.map((metric, index) => {
    const valueMap = new Map<string, string | ReactNode | undefined>()

    if (index === 0 && !showNewUI && !hideNodeContext) {
      valueMap.set(
        'Executed tool node',
        <NodeName name={node.current_node} type={node.current_node_type} key="current-node" />,
      )
      valueMap.set(
        'Previous node',
        <NodeName name={node.previous_node} type={node.previous_node_type} key="previous-node" />,
      )
      valueMap.set('Chosen path', node.chosen_path)
    }
    if (index === 0) {
      valueMap.set('Execution status', nodeConfig.execution_status)
    }
    // Text channels only, failures only. Voice's tool card is left exactly as it is.
    if (isTextAgentChannel(nodeConfig.channel) && metric.tool_call_error) {
      valueMap.set('Status', 'Failed')
    }
    valueMap.set(
      'Tool name',
      <TruncatedTextWithTooltip
        key={`tool-name-${index}`}
        tooltipContent={<span className="font-jetbrains text-xs-400">{metric.tool_name}</span>}
      >
        {metric.tool_name}
      </TruncatedTextWithTooltip>,
    )
    valueMap.set(
      'Tool input',
      <JsonExpandableView
        key={`tool-input-${index}`}
        data={JSON.stringify(metric.tool_input)}
        title="Tool Input"
      />,
    )
    valueMap.set(
      'Tool response',
      <JsonExpandableView
        key={`tool-response-${index}`}
        data={metric.tool_response}
        title="Tool Response"
      />,
    )
    valueMap.set('Time taken by tool to respond', `${(metric.time || 0).toFixed(3)}s`)

    return valueMap
  })
}

// Older runtimes emit engine vocabulary in skip reasons; render human copy
// regardless of source (the current runtime already emits plain copy).
export const humanizeSkipReason = (reason?: string): string | undefined => {
  if (!reason) return reason
  if (reason === 'run_if not met') return 'condition not met'
  if (reason === 'value cleared by regression') return 'outdated after a correction'
  return reason
}
